import { appContext } from "../AppContext";
import { IUserRights } from "../Providers.Api/Models.UserApi";
import { IUserDetails } from "../Providers.LocalStorage/Models";

export class RightsManager
{
    private _currentRights: (IUserRights[] | null) = null;
    public get currentRights(): IUserRights[]
    {
        if (!this._currentRights)
        {
            const ibssRights = appContext().localStorageProvider.getIbssRightList();
            const userDetails = appContext().localStorageProvider.getUserDetails();
            this._currentRights = [userDetails.userRights, ibssRights];
        }
        return this._currentRights;
    }
}

interface IRight
{
    type: RightType;
    parent?: IRight;
    readonly name: string;
    readonly fullName: string;
}

type RightType = 'Right' | 'RightWithApplicableBuilding';

class Right implements IRight
{
    public type: RightType = 'Right';
    public parent?: IRight;
    public readonly name: string;

    private constructor(name: string)
    {
        this.name = name;
    }

    public get fullName(): string
    {
        return (this.parent ? `${this.parent.fullName}.${this.name}` : this.name);
    }

    public static create(name: string): Right
    {
        return new Right(name);
    }

    public children<T extends Record<string, IRight>>(value: T): Right & T
    {
        return this.mergeChildren(this, value) as Right & T;
    }

    public mergeChildren<T extends Record<string, IRight>>(parent: IRight, children: T): IRight & T
    {
        Object.values(children).forEach(i => i.parent = parent);
        const parentAndChildren = Object.assign(parent, children);
        return parentAndChildren;
    }

    public has(): boolean
    {
        let hasRight: boolean;
        try
        {
            const parts = this.fullName.split('.');
            const area = parts[0];
            const entity = parts[1];
            const action = parts[2];
            const currentRights = appContext().rights.currentRights as any as Record<string, Record<string, string[]>>[];

            if (area && entity && action)
            {
                hasRight = currentRights.some(rights => rights[area][entity]?.some(i => i == action));
            }
            else if (area && entity)
            {
                hasRight = currentRights.some(rights => rights[area][entity]);
            }
            else if (area)
            {
                hasRight = currentRights.some(rights => rights[area]);
            }
            else
            {
                hasRight = false;
            }
        }
        catch
        {
            hasRight = false;
        }
        return hasRight;
    }

    public appliesTo(): RightWithApplicableBuilding
    {
        return new RightWithApplicableBuilding(this);
    }
}

class RightWithApplicableBuilding implements IRight
{
    private right: Right;

    public type: RightType = 'RightWithApplicableBuilding';

    public get parent() { return this.right.parent; }
    public set parent(value) { this.right.parent = value; }

    public get name() { return this.right.name; }
    public get fullName() { return this.right.fullName; }

    constructor(right: Right)
    {
        this.right = right;
    }

    public children<T extends Record<string, IRight>>(value: T): RightWithApplicableBuilding & T
    {
        return this.right.mergeChildren(this, value) as RightWithApplicableBuilding & T;
    }

    public has(buildingId: number): boolean
    {
        try
        {
            const hasRight = this.right.has();
            const applicableBuildings = appContext().rights.currentRights.flatMap(rights => rights.APPLIESTO[this.right.fullName] ?? []);
            const isBuildingApplicable = applicableBuildings.some(i => i == buildingId.toString());
            return (hasRight && isBuildingApplicable);
        }
        catch
        {
            return false;
        }
    }
}

export const rights = {
    ADMINPORTAL: Right.create('ADMINPORTAL').children({

        Bookings: Right.create('Bookings').children({

            Create: Right.create('Create'),
            Update: Right.create('Update'),
            Delete: Right.create('Delete'),
            V2: Right.create('V2'),
            Approve: Right.create('Approve'),
            Deny: Right.create('Deny'),
        }),
        Spaces: Right.create('Spaces').children({

            CheckIn: Right.create('CheckIn'),
            CheckOut: Right.create('CheckOut'),
            CheckInOnBehalfOf: Right.create('CheckInOnBehalfOf'),
            Create: Right.create('Create'),
            Update: Right.create('Update'),
            Delete: Right.create('Delete'),
            Parts: Right.create('Parts'),
            IsPartOf: Right.create('IsPartOf'),
        }),
        Tasks: Right.create('Tasks').children({

            Create: Right.create('Create'),
            Update: Right.create('Update'),
            Assign: Right.create('Assign'),
            Unassign: Right.create('Unassign'),
            Cancel: Right.create('Cancel'),
            Reallocate: Right.create('Reallocate'),
            SetInProgress: Right.create('SetInProgress'),
            ChangePriority: Right.create('ChangePriority'),
        }),
        Security: Right.create('Security').children({

            Delegates: Right.create('Delegates'),
            IdentityProviders: Right.create('IdentityProviders'),
            Roles: Right.create('Roles'),
            Users: Right.create('Users'),
        }),
        PortfolioSetup: Right.create('PortfolioSetup').children({

            Buildings: Right.create('Buildings'),
            Spaces: Right.create('Spaces'),
            BuildingKPIs: Right.create('BuildingKPIs'),
            VisitorTypes: Right.create('VisitorTypes'),
            SpacesStar: Right.create('Spaces/*'), // todo: should this be '...Spaces'?
            Parameters: Right.create('Parameters'),
            Devices: Right.create('Devices'),
            TaskTypes: Right.create('TaskTypes'),
            TaskCategories: Right.create('TaskCategories'),
            TaskStates: Right.create('TaskStates'),
            SpaceStates: Right.create('SpaceStates'),
            Notifications: Right.create('Notifications'),
            TagDefinitions: Right.create('TagDefinitions'),
        }),
        PortfolioManagement: Right.create('PortfolioManagement').children({

            Equipment: Right.create('Equipment'),
            NotificationReminders: Right.create('NotificationReminders'),
        }),
        Users: Right.create('Users').children({

            ShowUserPreferences: Right.create('ShowUserPreferences'),
            Create: Right.create('Create'),
            Update: Right.create('Update'),
            Delete: Right.create('Delete'),
        }),
        TaskTypes: Right.create('TaskTypes').children({

            Create: Right.create('Create'),
            Update: Right.create('Update'),
            Delete: Right.create('Delete'),
        }),
        TaskStates: Right.create('TaskStates').children({

            Create: Right.create('Create'),
            Update: Right.create('Update'),
            Delete: Right.create('Delete'),
        }),
        TaskCategories: Right.create('TaskCategories').children({

            Create: Right.create('Create'),
            Update: Right.create('Update'),
            Delete: Right.create('Delete'),
        }),
        Parameters: Right.create('Parameters').children({

            Create: Right.create('Create'),
            Update: Right.create('Update'),
            Delete: Right.create('Delete'),
        }),
        Devices: Right.create('Devices').children({

            Create: Right.create('Create'),
            Update: Right.create('Update'),
            Delete: Right.create('Delete'),
        }),
        SpaceStates: Right.create('SpaceStates').children({

            Create: Right.create('Create'),
            Update: Right.create('Update'),
            Delete: Right.create('Delete'),
        }),
        BuildingKPIs: Right.create('BuildingKPIs').children({

            Create: Right.create('Create'),
            Update: Right.create('Update'),
            Delete: Right.create('Delete'),
            Read: Right.create('Read'),
        }),
        Signage: Right.create('Signage').children({

            Create: Right.create('Create'),
            Update: Right.create('Update'),
            Delete: Right.create('Delete'),
        }),
        IsPart: Right.create('IsPart').children({

            Create: Right.create('Create'),
            Update: Right.create('Update'),
            Delete: Right.create('Delete'),
        }),
        Roles: Right.create('Roles').children({

            Create: Right.create('Create'),
        }),
        Notifications: Right.create('Notifications').children({

            Create: Right.create('Create'),
            Update: Right.create('Update'),
            Delete: Right.create('Delete'),
        }),
        IdentityProviders: Right.create('IdentityProviders').children({

            Create: Right.create('Create'),
            Update: Right.create('Update'),
            Delete: Right.create('Delete'),
        }),
        Files: Right.create('Files').children({

            Roamer: Right.create('Roamer'),
            Unity: Right.create('Unity'),
            Inspire: Right.create('Inspire'),
            Common: Right.create('Common'),
        }),
        CostCodes: Right.create('CostCodes').children({

            Read: Right.create('Read'),
        }),
        Manage: Right.create('Manage').children({

            CostCodes: Right.create('CostCodes'),
            BookingPolicies: Right.create('BookingPolicies'),
        }),
    }),
    ONELENS360: Right.create('ONELENS360').children({

        OperationalServices: Right.create('OperationalServices').children({

            VisitorPass: Right.create('VisitorPass').appliesTo(),
            Catering: Right.create('Catering'),
            Spaces: Right.create('Spaces'),
            SpaceLayouts: Right.create('SpaceLayouts'),
            Bookings: Right.create('Bookings'),
            ScheduleView: Right.create('ScheduleView'),
            Visitors: Right.create('Visitors'),
            CostCodes: Right.create('CostCodes'),
            Tasks: Right.create('Tasks'),
            Notifications: Right.create('Notifications'),
            DirectedCleaning: Right.create('DirectedCleaning'),
            OrphanedBookings: Right.create('OrphanedBookings'),
            OccupancyMonitor: Right.create('OccupancyMonitor'),
            Announcements: Right.create('Announcements'),
            Events: Right.create('Events'),
            CateringTemplates: Right.create('CateringTemplates')
        }),
        VisitorAnalytics: Right.create('VisitorAnalytics').children({

            Overview: Right.create('Overview'),
        }),
        SpaceAnalytics: Right.create('SpaceAnalytics').children({

            Analytics: Right.create('Analytics'),
            Overview: Right.create('Overview'),
            Heatmaps: Right.create('Heatmaps'),
            SpaceDetails: Right.create('SpaceDetails'),
        }),
        SustainabilityAnalytics: Right.create('SustainabilityAnalytics').children({

            Overview: Right.create('Overview'),
            Floors: Right.create('Floors'),
            Zones: Right.create('Zones'),
            Sensors: Right.create('Sensors'),
        }),
    }),
    FLEX: Right.create('FLEX').children({

        UserPreferences: Right.create('UserPreferences').children({

            Delegates: Right.create('Delegates'),
            Notifications: Right.create('Notifications'),
            Workplace: Right.create('Workplace'),
            AccountSettings: Right.create('AccountSettings'),
            CostCodes: Right.create('CostCodes'),
        }),
        Search: Right.create('Search').children({

            People: Right.create('People'),
            Spaces: Right.create('Spaces'),
            Equipment: Right.create('Equipment'),
        }),
        SearchPeople: Right.create('SearchPeople').children({

            Colleague: Right.create('Colleague'),
        }),
        SearchSpaces: Right.create('SearchSpaces').children({

            Single: Right.create('Single'),
            Advanced: Right.create('Advanced'),
        }),
        MySchedule: Right.create('MySchedule').children({

            MyBookings: Right.create('MyBookings'),
            MyVisitors: Right.create('MyVisitors'),
            MyBookingsForOthers: Right.create('MyBookingsForOthers'),
        }),
        MyTasks: Right.create('MyTasks').children({

            MyTasks: Right.create('MyTasks'),
        }),
        Events : Right.create('Events').children({
            View : Right.create('View')
        })
    }),
    API: Right.create('API').children({

        Bookings: Right.create('Bookings').appliesTo().children({

            V2: Right.create('V2'),
            Approve: Right.create('Approve').appliesTo(),
            Deny: Right.create('Deny').appliesTo(),
            Export: Right.create('Export'),
            BookOutsideOfficeHours: Right.create('BookOutsideOfficeHours').appliesTo(),
            BookOutsidePolicySlots: Right.create('BookOutsidePolicySlots').appliesTo(),
            MoveSpace: Right.create('MoveSpace'),
            AddOnlineMeetingLink: Right.create('AddOnlineMeetingLink'),
            BookOnBehalfOf: Right.create('BookOnBehalfOf'),
            AssignCostCode: Right.create('AssignCostCode'),
            BookRecurring: Right.create('BookRecurring'),
            BookOnBehalfAutoCheckinOption: Right.create('BookOnBehalfAutoCheckinOption').appliesTo(),
            IgnoreTimeHorizon: Right.create('IgnoreTimeHorizon').appliesTo(),
            OverrideSetupTeardown: Right.create('OverrideSetupTeardown').appliesTo(),
            BookOutsideWorkingHours: Right.create('BookOutsideWorkingHours').appliesTo(),
            BookLinkedSpaceWork: Right.create('BookLinkedSpaceWork'),
            BookLinkedSpaceSupport: Right.create('BookLinkedSpaceSupport'),
            BookLinkedSpaceAmenity: Right.create('BookLinkedSpaceAmenity'),
            IgnoreMinMaxDuration: Right.create('IgnoreMinMaxDuration').appliesTo(),
            UnlimitedCapacity: Right.create('UnlimitedCapacity').appliesTo(),
        }),
        Visits: Right.create('Visits').appliesTo().children({

            V2: Right.create('V2'),
            Export: Right.create('Export'),
            Import: Right.create('Import'),
            Approve: Right.create('Approve').appliesTo(),
            Deny: Right.create('Deny').appliesTo(),
            CheckIn: Right.create('CheckIn').appliesTo(),
            CheckOut: Right.create('CheckOut').appliesTo(),
        }),
        Tasks: Right.create('Tasks').appliesTo().children({

            Export: Right.create('Export'),
        }),
        Catering: Right.create('Catering').appliesTo().children({

            MultipleOrders: Right.create('MultipleOrders').appliesTo(),
            AssignCostCode: Right.create('AssignCostCode'),
            Approve: Right.create('Approve').appliesTo(),
            IgnoreCutOff: Right.create('IgnoreCutOff').appliesTo(),
        }),
        Spaces: Right.create('Spaces').appliesTo().children({

            Export: Right.create('Export'),
            CheckIn: Right.create('CheckIn').appliesTo(),
            CheckOut: Right.create('CheckOut').appliesTo(),
            CheckInOnBehalfOf: Right.create('CheckInOnBehalfOf').appliesTo(),
        }),
        CostCodes: Right.create('CostCodes').appliesTo().children({

            V2: Right.create('V2'),
        }),
    }),
    DATAMODEL: Right.create('DATAMODEL').children({

        Filters: Right.create('Filters').children({

            Read: Right.create('Read'),
        }),
        Visits: Right.create('Visits').children({

            Read: Right.create('Read'),
            Create: Right.create('Create'),
            Update: Right.create('Update'),
            Delete: Right.create('Delete'),
        }),
        Tasks: Right.create('Tasks').children({

            Create: Right.create('Create'),
            Update: Right.create('Update'),
        }),
        Tag2EventType: Right.create('Tag2EventType').children({

            Create: Right.create('Create'),
            Read: Right.create('Read'),
            Delete: Right.create('Delete'),
        }),
        TagDefinitions: Right.create('TagDefinitions').children({

            Read: Right.create('Read'),
        }),
        Bookings: Right.create('Bookings').children({

            Create: Right.create('Create'),
            Update: Right.create('Update'),
            Delete: Right.create('Delete'),
        }),
        Notes: Right.create('Notes').children({

            Create: Right.create('Create'),
            Update: Right.create('Update'),
            Delete: Right.create('Delete'),
        }),
        BookingPolicies: Right.create('BookingPolicies').children({

            Read: Right.create('Read'),
            Create: Right.create('Create'),
            Update: Right.create('Update'),
            Delete: Right.create('Delete'),
        }),
        Nodes: Right.create('Nodes').children({

            Create: Right.create('Create'),
            Update: Right.create('Update'),
            Delete: Right.create('Delete'),
        }),
        Equipment: Right.create('Equipment').children({

            Create: Right.create('Create'),
            Update: Right.create('Update'),
            Delete: Right.create('Delete'),
        }),
        NotificationReminders: Right.create('NotificationReminders').children({

            Create: Right.create('Create'),
            Update: Right.create('Update'),
            Delete: Right.create('Delete'),
        }),
        Roles: Right.create('Roles').children({

            Create: Right.create('Create'),
            Update: Right.create('Update'),
            Delete: Right.create('Delete'),
        }),
        BuildingKPIs: Right.create('BuildingKPIs').children({

            Create: Right.create('Create'),
            Update: Right.create('Update'),
            Delete: Right.create('Delete'),
            Read: Right.create('Read'),
        }),
        Spaces: Right.create('Spaces').children({

            Create: Right.create('Create'),
            Read: Right.create('Read'),
            Update: Right.create('Update'),
        }),
        CateringRestrictions: Right.create('CateringRestrictions').children({

            Create: Right.create('Create'),
            Read: Right.create('Read'),
            Update: Right.create('Update'),
            Delete: Right.create('Delete'),
        }),
        CateringMenuItems: Right.create('CateringMenuItems').children({

            Create: Right.create('Create'),
            Read: Right.create('Read'),
            Update: Right.create('Update'),
            Delete: Right.create('Delete'),
        }),
        CateringMenus: Right.create('CateringMenus').children({

            Create: Right.create('Create'),
            Read: Right.create('Read'),
            Update: Right.create('Update'),
            Delete: Right.create('Delete'),
        }),
        CateringOrderPolicies: Right.create('CateringOrderPolicies').children({

            Create: Right.create('Create'),
            Read: Right.create('Read'),
            Update: Right.create('Update'),
            Delete: Right.create('Delete'),
        }),
        CateringOrders: Right.create('CateringOrders').children({

            Create: Right.create('Create'),
            Read: Right.create('Read'),
            Update: Right.create('Update'),
            Delete: Right.create('Delete'),
        }),
        CateringSuppliers: Right.create('CateringSuppliers').children({

            Create: Right.create('Create'),
            Read: Right.create('Read'),
            Update: Right.create('Update'),
            Delete: Right.create('Delete'),
        }),
        Notifications: Right.create('Notifications').children({

            Read: Right.create('Read'),
            Create: Right.create('Create'),
            Update: Right.create('Update'),
            Delete: Right.create('Delete'),
        }),
        IdentityProviders: Right.create('IdentityProviders').children({

            Read: Right.create('Read'),
            Create: Right.create('Create'),
            Update: Right.create('Update'),
            Delete: Right.create('Delete'),
        }),
        TicketedEvents : Right.create('TicketedEvents').children({
            Read: Right.create('Read'),
            Create: Right.create('Create'),
            Update: Right.create('Update'),
            Delete: Right.create('Delete'),
        }),
        CateringTemplates: Right.create('CateringTemplates').children({
            Create: Right.create('Create'),
            Read: Right.create('Read'),
            Update: Right.create('Update'),
            Delete: Right.create('Delete'),     
        })
    }),
};
